import styled, { css } from 'styled-components';

import { CHART_TONES, ChartTone, chartFaded } from './chartTokens';

/**
 * ТЗ 0103 — вёрстка общих графиков. Молекула не может брать токены страниц
 * (`_detailShared/*.styled` — уровень страниц), поэтому здесь те же значения
 * цветов и отступов, что у `DS.Table` / `S.Card`, — одна система на глаз.
 */

const TEXT = '#0f172a';
const MUTED = '#64748b';
const LINE = '#e2e8f0';
const TRACK = '#f1f5f9';
const FOCUS = '#2563eb';
const ACCENT = CHART_TONES.accent;

/** Холст chart.js растягивается на высоту карточки (`ChartCard`). */
export const ChartBox = styled.div`
  position: relative;
  height: 100%;
  min-height: 0;
`;

export const ChartEmpty = styled.div`
  padding: 12px 0;
  color: ${MUTED};
  font-size: 12px;
`;

export const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

export const SrCaption = styled.caption`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

/* ── Спарклайн ───────────────────────────────────────────────────────── */

export const SparkSvg = styled.svg`
  display: inline-block;
  vertical-align: middle;
  overflow: visible;
  flex: 0 0 auto;
`;

/* ── Полосы (RankBars) и воронка (FunnelBars) ────────────────────────── */

export const RankList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const FunnelList = styled(RankList)`
  gap: 6px;
`;

export const RankItem = styled.li`
  min-width: 0;
`;

const rowFace = css`
  display: block;
  width: 100%;
  padding: 6px 8px;
  border-radius: 6px;
  text-align: left;
  font: inherit;
  color: ${TEXT};
`;

export const RankStatic = styled.div`
  ${rowFace};
`;

export const RankButton = styled.button<{ $selected?: boolean }>`
  ${rowFace};
  border: 0;
  background: ${({ $selected }) => ($selected ? '#eff6ff' : 'transparent')};
  cursor: pointer;

  &:hover {
    background: #f8fafc;
  }

  &:focus-visible {
    outline: 2px solid ${FOCUS};
    outline-offset: -2px;
  }
`;

export const RankHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
  font-size: 13px;
`;

export const RankLabel = styled.span<{ $highlight?: boolean }>`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: ${({ $highlight }) => ($highlight ? 700 : 500)};
`;

export const RankValue = styled.span<{ $muted?: boolean }>`
  flex: 0 0 auto;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: ${({ $muted }) => ($muted ? MUTED : TEXT)};
`;

const TONE_TEXT: Record<ChartTone, string> = {
  good: '#15803d',
  bad: '#b91c1c',
  warn: '#b45309',
  neutral: MUTED,
  accent: '#1d4ed8',
};

export const RankDelta = styled.span<{ $tone: ChartTone }>`
  margin-left: 6px;
  font-size: 11px;
  font-weight: 600;
  color: ${({ $tone }) => TONE_TEXT[$tone]};
`;

export const RankTrack = styled.div`
  position: relative;
  height: 8px;
  margin-top: 4px;
  border-radius: 4px;
  background: ${TRACK};
`;

export const RankFill = styled.div<{ $width: number; $color?: string }>`
  height: 100%;
  width: ${({ $width }) => $width}%;
  border-radius: 4px;
  background: ${({ $color }) => $color ?? ACCENT};
`;

/** Отметка сравнения — тонкая тёмная черта поперёк дорожки. */
export const RankCompare = styled.span<{ $at: number }>`
  position: absolute;
  top: -2px;
  bottom: -2px;
  left: ${({ $at }) => `calc(${$at}% - 1px)`};
  width: 2px;
  border-radius: 1px;
  background: ${TEXT};
  opacity: 0.55;
`;

export const RankSecondary = styled.div`
  margin-top: 2px;
  font-size: 11px;
  color: ${MUTED};
`;

export const FunnelShare = styled.span`
  margin-left: 8px;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
`;

export const FunnelTrack = styled.div`
  position: relative;
  height: 14px;
  margin-top: 4px;
  border-radius: 4px;
  background: ${TRACK};
`;

export const FunnelFill = styled.div<{ $width: number; $color?: string }>`
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: 0;
  width: ${({ $width }) => $width}%;
  border-radius: 3px;
  background: ${({ $color }) => $color ?? ACCENT};
`;

/** Сравнение — бледный столбик на всю высоту дорожки под текущим. */
export const FunnelCompare = styled.div<{ $width: number; $color?: string }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: ${({ $width }) => $width}%;
  border-radius: 4px;
  background: ${({ $color }) => chartFaded($color ?? ACCENT)};
`;

/* ── Тепловая матрица ────────────────────────────────────────────────── */

/** Одна синяя шкала, как у матрицы «канал × курс»; текст тёмный везде. */
export const HEAT_TONE_BG: ReadonlyArray<string> = [
  'transparent',
  '#f5f9ff',
  '#e8f1fe',
  '#d6e6fd',
  '#c7dcfb',
];

const heatBg = (tone: number): string =>
  HEAT_TONE_BG[Math.max(0, Math.min(HEAT_TONE_BG.length - 1, tone))];

export const HeatScroll = styled.div`
  width: 100%;
  overflow-x: auto;
`;

export const HeatTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    border-bottom: 1px solid ${LINE};
  }
`;

/**
 * Финальная проверка 0103 (F-E14): колонка строк матрицы закреплена при
 * прокрутке вбок (когорты на 1100 — как таблица «Рекламы»): подпись строки
 * видна у любой колонки.
 */
const heatSticky = css`
  position: sticky;
  left: 0;
  z-index: 1;
  background: #fff;
  box-shadow: inset -1px 0 0 ${LINE};
`;

export const HeatCorner = styled.th`
  ${heatSticky}
  width: 100%;
  padding: 8px 12px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${MUTED};
  white-space: nowrap;
`;

export const HeatHead = styled.th<{ $highlight?: boolean }>`
  padding: 8px 12px;
  text-align: right;
  font-size: 11px;
  font-weight: 600;
  color: ${({ $highlight }) => ($highlight ? TEXT : MUTED)};
  white-space: nowrap;
  ${({ $highlight }) =>
    $highlight &&
    css`
      background: #f8fafc;
    `}
`;

export const HeatRowHead = styled.th`
  ${heatSticky}
  padding: 8px 12px;
  text-align: left;
  font-weight: 500;
  color: ${TEXT};
  white-space: nowrap;
`;

export const HeatCell = styled.td<{
  $tone: number;
  $muted?: boolean;
  $highlight?: boolean;
}>`
  padding: 0;
  min-width: 88px;
  text-align: right;
  font-variant-numeric: tabular-nums;
  background: ${({ $tone }) => heatBg($tone)};
  ${({ $highlight }) =>
    $highlight &&
    css`
      box-shadow: inset 1px 0 0 ${LINE}, inset -1px 0 0 ${LINE};
    `}
  ${({ $muted }) =>
    $muted &&
    css`
      color: #94a3b8;
      background: #f8fafc;
    `}
`;

const cellFace = css`
  display: block;
  width: 100%;
  padding: 8px 12px;
  white-space: nowrap;
`;

export const HeatValue = styled.span`
  ${cellFace};
`;

export const HeatEmpty = styled.span`
  ${cellFace};
  color: #94a3b8;
`;

export const HeatSecondary = styled.span`
  display: block;
  font-size: 11px;
  color: ${MUTED};
`;

export const HeatButton = styled.button`
  ${cellFace};
  border: 0;
  background: transparent;
  font: inherit;
  color: inherit;
  text-align: right;
  cursor: pointer;

  &:hover {
    box-shadow: inset 0 0 0 1px #93c5fd;
  }

  &:focus-visible {
    outline: 2px solid ${FOCUS};
    outline-offset: -2px;
  }
`;
